import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, FileText, Image, Link as LinkIcon, Paperclip } from 'lucide-react';

import store, { useAppSelector } from '../../../store';
import { PhotoImage } from '../../../components/common/PhotoImage';
import { Button } from '../../../components/ui/button';
import Toast from '../../../components/Toast/Toast';
import { Admin_SavePost } from '../../../constants/adminData';
import { getSensitiveData } from '@/utils/secureStorage';
import { UserProfileData } from '@/types/admin';
import DashboardLayout from '../../../components/DashboardLayout';
import {
  PostEditor,
  PostEditorHandle,
  AttachmentUploader,
  getPostWordCount,
  VisibilitySelector,
  VisibilityOption,
} from './PostComponents';
import styles from './createPost.module.css';
import imageCompression from 'browser-image-compression';
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    [{ align: [] }],
    ['clean'],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const quillFormats = [
  'bold',
  'italic',
  'underline',
  'list',
  'image',
  'link',
  'align',
];

interface CreatePostProps {
  onBack: () => void;
  onContinue?: () => void;
}

type ToastType = 'success' | 'error' | 'warning';

// Matches the post-media limit enforced by the API.
const MAX_POST_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_POST_FILE_SIZE_LABEL = '5 MB';
const MAX_POST_IMAGE_COUNT = 3;
const MAX_POST_WORD_COUNT = 2000;
const MAX_COMPRESSED_IMAGE_SIZE_MB = 1.5;
const MAX_TOTAL_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;


const compressImage = async (file: File): Promise<File> => {
  // Only compress supported normal web images
  const supportedTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
  ];

  if (!supportedTypes.includes(file.type)) {
    return file;
  }

  // Keep small images unchanged
  if (file.size <= MAX_COMPRESSED_IMAGE_SIZE_MB * 1024 * 1024) {
    return file;
  }

  try {
    const compressedBlob = await imageCompression(file, {
      maxSizeMB: MAX_COMPRESSED_IMAGE_SIZE_MB,
      maxWidthOrHeight: 2048,
      useWebWorker: true,
      initialQuality: 0.85,

      // Important: preserve type
      fileType: file.type,
    });

    // Recreate File so backend receives original filename/type
    const compressedFile = new File(
      [compressedBlob],
      file.name,
      {
        type: file.type,
        lastModified: file.lastModified,
      }
    );

    console.log('IMAGE COMPRESSION', {
      name: compressedFile.name,
      type: compressedFile.type,
      originalMB:
        (file.size / 1024 / 1024).toFixed(2),
      compressedMB:
        (compressedFile.size / 1024 / 1024).toFixed(2),
    });

    // Don't use compressed version if somehow larger
    if (compressedFile.size >= file.size) {
      return file;
    }

    return compressedFile;

  } catch (error) {
    console.error('Image compression failed:', error);

    return file;
  }
};
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

function VisibilityCard({
  label,
  description,
  selected,
  onSelect,
}: {
  label: VisibilityOption;
  description: string;
  selected: boolean;
  onSelect: (value: VisibilityOption) => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.visibilityCard} ${selected ? styles.visibilitySelected : ''}`}
      onClick={() => onSelect(label)}
    >
      <div>
        <p className={styles.visibilityLabel}>{label}</p>
        <p className={styles.visibilityDescription}>{description}</p>
      </div>
    </button>
  );
}

function TipsPanel() {
  return (
    <aside className={styles.tipsPanel}>
      <div className={styles.card}>
        <h3 className={styles.cardTitle}>Tips for a great post</h3>
        <div className={styles.tipItem}>
          <strong>Be authentic</strong>
          <p>Share your thoughts and experiences.</p>
        </div>
        <div className={styles.tipItem}>
          <strong>Add media</strong>
          <p>Images and videos get more engagement.</p>
        </div>
        <div className={styles.tipItem}>
          <strong>Ask questions</strong>
          <p>Encourage others to engage.</p>
        </div>
        <div className={styles.tipItem}>
          <strong>Keep it relevant</strong>
          <p>Add value to your network.</p>
        </div>
      </div>
    </aside>
  );
}

function MobileHeader({
  onBack,
  onContinue,
}: {
  onBack: () => void;
  onContinue?: () => void;
}) {
  return (
    <div className={styles.mobilePostHeader}>
      <button className={styles.mobileIconButton} type="button" onClick={onBack} aria-label="Go back">
        <ArrowLeft className={styles.mobileHeaderIcon} />
      </button>
      <h1 className={styles.mobilePostTitle}>Create Post</h1>
      <button
        className={styles.mobilePostsButton}
        type="button"
        onClick={onContinue}
        aria-label="View posts"
      >
        <FileText className={styles.mobileHeaderIcon} />
        <span>Posts</span>
      </button>
    </div>
  );
}

export default function CreatePost({ onBack, onContinue }: CreatePostProps) {
  const profile = useAppSelector((state) => state.userProfile);
  const [userData, setUserData] = useState<UserProfileData | null>(null);

  const [visibility, setVisibility] = useState<VisibilityOption>('Public');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [postContent, setPostContent] = useState('');
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkValue, setLinkValue] = useState('');
  const [isPosting, setIsPosting] = useState(false);

  const editorRef = useRef<PostEditorHandle | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const userName =
    userData?.name ||
    `${profile.personalInfo.firstName || ''} ${profile.personalInfo.lastName || ''}`.trim() ||
    'Guest User';

  useEffect(() => {
    const loadProfile = async () => {
      const stored = await getSensitiveData('userData');
      if (!stored) return;
      setUserData(JSON.parse(stored));
    };
    loadProfile();
  }, []);

  const showToastMessage = (message: string, type: ToastType) => {
    setToast({ message, type });
  };

  const getValidFiles = (files: File[]) => {
    const oversizedFiles = files.filter((file) => file.size > MAX_POST_FILE_SIZE_BYTES);

    if (oversizedFiles.length > 0) {
      const fileLabel = oversizedFiles.length === 1 ? 'image' : 'images';
      showToastMessage(
        `The selected ${fileLabel} exceed${oversizedFiles.length === 1 ? 's' : ''} the ${MAX_POST_FILE_SIZE_LABEL} limit. Please choose a smaller file.`,
        'error'
      );
    }

    return files.filter((file) => file.size <= MAX_POST_FILE_SIZE_BYTES);
  };

  const limitImagesPerPost = (files: File[]) => {
    let remainingImageSlots = MAX_POST_IMAGE_COUNT - selectedFiles.filter((file) => file.type.startsWith('image/')).length;
    let skippedImage = false;

    const allowedFiles = files.filter((file) => {
      if (!file.type.startsWith('image/')) return true;
      if (remainingImageSlots <= 0) {
        skippedImage = true;
        return false;
      }
      remainingImageSlots -= 1;
      return true;
    });

    if (skippedImage) {
      showToastMessage(`You can add up to ${MAX_POST_IMAGE_COUNT} images to a post.`, 'warning');
    }

    return allowedFiles;
  };

  const handleFilesChange = async (files: File[]) => {
    const validFiles = limitImagesPerPost(getValidFiles(files));

    if (validFiles.length === 0) return;

    try {
      const processedFiles = await Promise.all(
        validFiles.map(async (file) => {
          if (file.type.startsWith('image/')) {
            return await compressImage(file);
          }

          return file;
        })
      );

      setSelectedFiles((prev) => [...prev, ...processedFiles]);
    } catch (error) {
      console.error('FILE PROCESSING ERROR:', error);
      showToastMessage('Failed to process selected images.', 'error');
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const handleRemoveEditorImage = (imageIndex: number) => {
    setSelectedFiles((prev) => {
      let currentImageIndex = 0;

      return prev.filter((file) => {
        if (!file.type.startsWith('image/')) return true;
        const shouldRemove = currentImageIndex === imageIndex;
        currentImageIndex += 1;
        return !shouldRemove;
      });
    });
  };

  const handleAddImage = async (file: File) => {
    const validFiles = limitImagesPerPost(getValidFiles([file]));

    if (validFiles.length === 0) return;

    try {
      const compressedFile = await compressImage(validFiles[0]);

      const imageFiles = selectedFiles.filter((item) =>
        item.type.startsWith('image/')
      );

      const imageIndex = imageFiles.length;

      setSelectedFiles((prev) => [...prev, compressedFile]);

      editorRef.current?.insertImage(
        compressedFile,
        imageIndex
      );
    } catch (error) {
      console.error('IMAGE PROCESSING ERROR:', error);

      showToastMessage(
        'Failed to process the selected image.',
        'error'
      );
    }
  };

  const handleAddLink = () => {
    if (!linkValue.trim()) return;
    editorRef.current?.insertLink(linkValue.trim());
    setLinkValue('');
    setShowLinkModal(false);
  };

  const buildPostPayload = async () => {
    const currentProfile = store.getState().userProfile;
    const userID = currentProfile?.userId || (await getSensitiveData('userId'));
    const editor = editorRef.current?.getEditorRoot();
    if (!editor) return null;

    let displayOrder = 1;
    let attachmentIdCounter = 1;

    const contentBlocks: any[] = [];
    const attachments: any[] = [];

    const imageFiles = selectedFiles.filter((file) => file.type.startsWith('image/'));

    const visitNode = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
        contentBlocks.push({
          blockId: 0,
          blockType: 'TEXT',
          textContent: node.textContent.trim(),
          attachmentId: null,
          metaData: null,
          displayOrder: displayOrder++,
        });
        return;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const tagName = element.tagName.toUpperCase();

        if (element.dataset.editorImageRemove !== undefined) return;

        if (tagName === 'IMG') {
          const attachmentId = Number(element.dataset.attachmentId) || attachmentIdCounter++;
          const imageIndex = Number(element.dataset.newFileIndex);
          const imageFile = imageFiles[imageIndex];
          const imageElement = element as HTMLImageElement;

          contentBlocks.push({
            blockId: 0,
            blockType: 'IMAGE',
            textContent: null,
            attachmentId,
            metaData: null,
            displayOrder: displayOrder++,
          });

          attachments.push({
            attachmentId,
            fileType: 'IMAGE',
            fileName: imageFile?.name,
            contentType: imageFile?.type,
            fileSize: imageFile?.size
          });
          return;
        }

        if (tagName === 'A') {
          contentBlocks.push({
            blockId: 0,
            blockType: 'LINK',
            textContent: element.getAttribute('href') || element.innerText,
            attachmentId: null,
            metaData: null,
            displayOrder: displayOrder++,
          });
          return;
        }

        element.childNodes.forEach(visitNode);
      }
    };

    Array.from(editor.childNodes).forEach(visitNode);

    // =========================================
    // FILE BLOCKS
    // =========================================

    const nonImageFiles = selectedFiles.filter(
      (file) => !file.type.startsWith('image/')
    );

    nonImageFiles.forEach((file) => {
      const attachmentId = attachmentIdCounter++;

      attachments.push({
        attachmentId,
        fileType: 'FILE',
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
        displayOrder: displayOrder,
      });

      contentBlocks.push({
        blockId: 0,
        blockType: 'FILE',
        textContent: null,
        attachmentId,
        metaData: JSON.stringify({
          fileName: file.name,
        }),
        displayOrder: displayOrder++,
      });
    });

    const content = contentBlocks
      .filter((block) => block.blockType === 'TEXT')
      .map((block) => block.textContent)
      .join(' ');

    return {
      userId: userID,
      content,
      postTypeId: 1,
      visibilityId: visibility === 'Public' ? 1 : visibility === 'Your Network' ? 2 : 3,
      attachments,
      contentBlocks,
    };
  };

  const handlePost = async () => {
    if (isPosting) return;

    if (getPostWordCount(postContent) > MAX_POST_WORD_COUNT) {
      showToastMessage(`Post content cannot exceed ${MAX_POST_WORD_COUNT}.`, 'warning');
      return;
    }

    try {
      setIsPosting(true);

      if (
        selectedFiles.some(
          (file) => file.size > MAX_POST_FILE_SIZE_BYTES
        )
      ) {
        showToastMessage(
          `Images must be ${MAX_POST_FILE_SIZE_LABEL} or smaller.`,
          'error'
        );
        return;
      }

      const totalUploadSize = selectedFiles.reduce(
        (total, file) => total + file.size,
        0
      );

      if (totalUploadSize > MAX_TOTAL_UPLOAD_SIZE_BYTES) {
        showToastMessage(
          'Total attachment size cannot exceed 10 MB.',
          'error'
        );
        return;
      }

      const payload = await buildPostPayload();
      if (!payload || payload.contentBlocks.length === 0) {
        showToastMessage('Post cannot be empty', 'warning');
        return;
      }
      const formData = new FormData();
      formData.append('userId', payload.userId!);
      formData.append('content', payload.content);
      formData.append('postTypeId', String(payload.postTypeId));
      formData.append(
        'visibilityId',
        String(payload.visibilityId)
      );

      formData.append(
        'contentBlocks',
        JSON.stringify(payload.contentBlocks)
      );

      formData.append(
        'attachments',
        JSON.stringify(payload.attachments)
      );

      selectedFiles.forEach((file) => {
        formData.append('files', file, file.name);
      });
      const res = await Admin_SavePost(formData);

      if (!res?.success) {
        showToastMessage(
          res?.message || 'Something went wrong',
          'error'
        );
        return;
      }

      setSelectedFiles([]);
      setPostContent('');

      showToastMessage(
        'Post created successfully!',
        'success'
      );
    } catch (error) {
      console.error('POST ERROR:', error);

      const message =
        typeof error === 'object' &&
          error !== null &&
          'message' in error
          ? String(error.message)
          : 'Failed to post';

      showToastMessage(message, 'error');
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <DashboardLayout title="Create Post">
      <div className={styles.pageWrapper}>
        <div className={styles.columns}>
          <main className={styles.mainColumn}>
            <div className={styles.card}>
              <MobileHeader onBack={onBack} onContinue={onContinue} />
              <div className={styles.postTopRow}>
                <button className={styles.backButton} type="button" onClick={onBack}>
                  ← Cancel
                </button>
                <button className={styles.secondaryButton} type="button" onClick={onContinue}>
                  View Posts
                </button>
              </div>
              <div className={styles.profileBlock}>
                <div className={styles.profileAvatarLarge}>
                  {userData?.photoId ? (
                    <PhotoImage
                      photoId={userData.photoId}
                      alt={userName}
                      className={styles.profileAvatarImage}
                    />
                  ) : (
                    <span>{getInitials(userName)}</span>
                  )}
                </div>
                <div>
                  <p className={styles.profileName}>{userName}</p>
                  <p className={styles.profileRole}>{userData?.itSpecialtyName || 'AI Engineer'}</p>
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor="postContent">
                  What's on your mind?
                </label>
                <PostEditor
                  ref={editorRef}
                  value={postContent}
                  onChange={setPostContent}
                  placeholder="Share your thoughts, add images, links, and more."
                  maxWords={MAX_POST_WORD_COUNT}
                  onMaxWordsReached={() =>
                    showToastMessage(`Post content cannot exceed ${MAX_POST_WORD_COUNT}.`, 'warning')
                  }
                  onRemoveImage={handleRemoveEditorImage}
                />
                <div className={styles.charCount}>
                  {getPostWordCount(postContent)}/{MAX_POST_WORD_COUNT}
                </div>
              </div>
              <AttachmentUploader
                files={selectedFiles}
                onFilesChange={handleFilesChange}
                onRemoveFile={handleRemoveFile}
                onAddImage={handleAddImage}
                onAddLink={() => setShowLinkModal(true)}
              />
              <div className={styles.visibilityGrid}>
                <VisibilityCard
                  label="Public"
                  description="Anyone on or off ISentryx"
                  selected={visibility === 'Public'}
                  onSelect={setVisibility}
                />
                <VisibilityCard
                  label="Your Network"
                  description="Only people in your network"
                  selected={visibility === 'Your Network'}
                  onSelect={setVisibility}
                />
                <VisibilityCard
                  label="Only Me"
                  description="Private post only for you"
                  selected={visibility === 'Only Me'}
                  onSelect={setVisibility}
                />
              </div>
              <Button
                className={styles.postButton}
                onClick={handlePost}
                disabled={isPosting}
              >
                {isPosting ? 'Posting...' : 'Post'}
              </Button>
            </div>
          </main>
          <TipsPanel />
        </div>
      </div>

      {showLinkModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalCard}>
            <h2 className={styles.modalTitle}>Add Link</h2>
            <input
              type="url"
              value={linkValue}
              onChange={(e) => setLinkValue(e.target.value)}
              placeholder="https://example.com"
              className={styles.modalInput}
            />
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.modalSecondary}
                onClick={() => setShowLinkModal(false)}
              >
                Cancel
              </button>
              <button type="button" className={styles.modalPrimary} onClick={handleAddLink}>
                Add Link
              </button>
            </div>
          </div>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </DashboardLayout>
  );
}
